import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BusinessPartnerGroupModel from '../../models/BusinessPartnerGroupModel';
import BusinessPartnerModel from '../../models/BusinessPartnerModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import BusinessPartnerGroup_tab_BusinessPartners from './Tabs/BusinessPartnerGroup_tab_BusinessPartners';

const BusinessPartnerGroupView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<BusinessPartnerGroupModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<BusinessPartnerGroupModel>();

    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes?active=true&applicationFunction=' + ApplicationFunctions.UpdateBusinessPartnerGroup, '-- Select type');
    const employees = useSelectListOptions('api/v1/selectlist/Employees', '-- Select employee');

    let businessPartnerGroupId: number | undefined;
    let idParams: IdParams = useParams();
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        businessPartnerGroupId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (businessPartnerGroupId !== undefined) {
            DataService.getById<BusinessPartnerModel>('api/v1/BusinessPartnerGroups/', businessPartnerGroupId)
                .then(response => {
                    setFormData(response);
                })
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [businessPartnerGroupId]);

    function submit() {
        if (formData) {
            DataService
                .update<BusinessPartnerGroupModel>(`api/v1/BusinessPartnerGroups/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'businessPartnerTypeId', label: 'Type', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'referenceCode', label: 'Code', labelSize: 3 },
            { name: 'accountManagerEmployeeId', label: 'Account manager', labelSize: 3 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorized)
        return (<Unauthorized />);

    if (!formData)
        return <SpinnerOverlay />

    return (
        <div className='main-content'>
            <Form title='Business-partner group' footer={(<>
                <ButtonBar offset={3}>
                    <Authorize applicationFunction={ApplicationFunctions.UpdateBusinessPartner}>
                        <SaveButton  {...getSubmitButtonProps()} />
                    </Authorize>
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <SelectListField {...getFieldProps('businessPartnerTypeId')} options={businessPartnerTypes} />
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('referenceCode')} />
                            <SelectListField {...getFieldProps('accountManagerEmployeeId')} options={employees} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {businessPartnerGroupId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='main-tabs'
                                defaultActiveKey='tab_businesspartners'
                                tabs={[
                                    {
                                        title: 'BusinessPartners',
                                        eventKey: 'tab_businesspartners',
                                        component: <BusinessPartnerGroup_tab_BusinessPartners businessPartnerGroupId={businessPartnerGroupId} businessPartnerTypeId={formData.businessPartnerTypeId} />
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default BusinessPartnerGroupView;